/* eslint-disable */
// Session 55 · Phase 4 — Alpha Users CRM.
// Sortable table of every user with cohort/invitation/device/first-x/
// bookings/community/messages/feedback/notes. Click a row to open the
// drill-down drawer with editable cohort + invite status + notes.

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const COHORTS = ["prelaunch", "wave1", "wave2", "founder", "team"];
const INVITE_STATUSES = ["pending", "invited", "accepted", "churned"];

const short = (s, n = 24) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "—");
const dateOnly = (s) => (s ? String(s).slice(0, 10) : "—");
const clockTime = (s) => (s ? String(s).slice(11, 16) : "");

export default function AdminAlphaUsers() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState({ cohort: "", invite_status: "" });
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [rosterCohort, setRosterCohort] = useState("wave1");
  const [roster, setRoster] = useState(null);

  const loadRoster = async (cohort) => {
    try {
      const r = await api.get(`/admin/alpha-invites/roster/${cohort}`);
      setRoster(r.data);
    } catch (e) { /* ignore — roster is optional */ }
  };
  useEffect(() => {
    if (user?.role === "admin") loadRoster(rosterCohort);
  }, [user, rosterCohort]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.cohort) params.set("cohort", filter.cohort);
      if (filter.invite_status) params.set("invite_status", filter.invite_status);
      const r = await api.get(`/admin/alpha-users?${params}`);
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user, filter]);

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const s = q.trim().toLowerCase();
    if (!s) return data.users;
    return data.users.filter(u =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.city || "").toLowerCase().includes(s),
    );
  }, [data, q]);

  const patch = async (uid, body) => {
    setBusy(true); setErr("");
    try {
      const r = await api.patch(`/admin/alpha-users/${uid}`, body);
      setSelected(r.data.user);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div style={{padding:40}}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div style={{padding:"32px 40px", maxWidth:1600, margin:"0 auto", fontFamily:"Jost, sans-serif", color:"#1B2340"}} data-testid="admin-alpha-users">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16}}>
        <div>
          <span className="m-eyebrow" style={{color:"var(--gold)"}}>Admin · Alpha Users</span>
          <h1 style={{fontFamily:"Cormorant Garamond, serif", fontSize:32, margin:"4px 0 0", fontWeight:500}}>Wave Tracker</h1>
        </div>
        <div style={{display:"flex", gap:16, alignItems:"center"}}>
          <button type="button" onClick={() => setShowInvite(true)} data-testid="open-bulk-invite"
            style={{padding:"10px 22px", background:"#1B2340", color:"#FAF8F3", border:"none", borderRadius:999, cursor:"pointer", fontSize:13, fontWeight:500, letterSpacing:"0.02em"}}>
            + Bulk Invite
          </button>
          <Link to="/admin/command-center" style={{color:"#B8913A", fontSize:13}}>← Command Center</Link>
        </div>
      </div>

      <WaveRoster
        roster={roster}
        cohort={rosterCohort}
        onCohortChange={setRosterCohort}
        onRefresh={() => loadRoster(rosterCohort)}
      />

      <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:14, alignItems:"center"}}>
        <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name / email / city…"
          data-testid="alpha-users-search"
          style={{padding:"8px 12px", border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, minWidth:280, fontSize:13}} />
        <select value={filter.cohort} onChange={(e) => setFilter({...filter, cohort: e.target.value})}
          data-testid="alpha-users-filter-cohort"
          style={{padding:"8px 10px", border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13}}>
          <option value="">All cohorts</option>
          {COHORTS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.invite_status} onChange={(e) => setFilter({...filter, invite_status: e.target.value})}
          data-testid="alpha-users-filter-status"
          style={{padding:"8px 10px", border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13}}>
          <option value="">All statuses</option>
          {INVITE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{marginLeft:"auto", fontSize:12, color:"rgba(27,35,64,0.6)"}}>
          {filtered.length} of {data?.total || 0} users
        </span>
      </div>

      {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginBottom:12}}>{err}</div>}

      <div style={{background:"#fff", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}} data-testid="alpha-users-table">
            <thead>
              <tr style={{textAlign:"left", background:"rgba(184,145,58,0.06)", borderBottom:"1px solid rgba(184,145,58,0.25)"}}>
                <th style={th}>Member</th>
                <th style={th}>Cohort</th>
                <th style={th}>Invite</th>
                <th style={th}>Joined</th>
                <th style={th}>Last active</th>
                <th style={th}>Profile</th>
                <th style={th}>Legal</th>
                <th style={th}>Platform</th>
                <th style={th}>Bookings</th>
                <th style={th}>Posts</th>
                <th style={th}>Msgs</th>
                <th style={th}>Bugs / FR</th>
                <th style={th}>Sessions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const platform = u.device?.platform || "web";
                const legal = u.legal_ok ? "✓" : "…";
                return (
                  <tr key={u.user_id} onClick={() => setSelected(u)}
                    data-testid={`alpha-user-row-${u.user_id}`}
                    style={{borderBottom:"1px solid rgba(27,35,64,0.06)", cursor:"pointer", background: selected?.user_id === u.user_id ? "rgba(184,145,58,0.06)" : "transparent"}}>
                    <td style={td}>
                      <div style={{fontWeight:500}}>{u.name || u.email}</div>
                      <div style={{fontSize:11, color:"rgba(27,35,64,0.55)"}}>{short(u.email, 30)}</div>
                    </td>
                    <td style={td}><Pill label={u.invitation?.cohort} /></td>
                    <td style={td}><Pill label={u.invitation?.invite_status} accent /></td>
                    <td style={td}>{dateOnly(u.created_at)}</td>
                    <td style={td}>{dateOnly(u.activity?.last_login_at)}</td>
                    <td style={td}>{u.profile_completion_pct}%</td>
                    <td style={td}><span style={{color: u.legal_ok ? "#83B26E" : "#B8913A"}}>{legal}</span></td>
                    <td style={td}>{platform}</td>
                    <td style={td}>{u.counts?.bookings || 0}</td>
                    <td style={td}>{u.counts?.community_posts || 0}</td>
                    <td style={td}>{u.counts?.messages || 0}</td>
                    <td style={td}>{u.counts?.bug_reports || 0} / {u.counts?.feature_requests || 0}</td>
                    <td style={td}>{u.activity?.total_sessions || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <UserDrawer user={selected} onClose={() => setSelected(null)}
          onPatch={patch} busy={busy} />
      )}

      {showInvite && (
        <BulkInviteModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); load(); loadRoster(rosterCohort); }}
        />
      )}
    </div>
  );
}

const th = {padding:"10px 8px", fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(27,35,64,0.7)"};
const td = {padding:"10px 8px", color:"#1B2340"};

function Pill({label, accent}) {
  if (!label) return <span style={{color:"rgba(27,35,64,0.4)"}}>—</span>;
  return (
    <span style={{
      display:"inline-block", padding:"2px 10px", borderRadius:999,
      fontSize:11, letterSpacing:"0.02em",
      background: accent ? "rgba(27,35,64,0.08)" : "rgba(184,145,58,0.14)",
      color: accent ? "#1B2340" : "#8A6B26",
    }}>{label}</span>
  );
}

function UserDrawer({user, onClose, onPatch, busy}) {
  const [cohort, setCohort] = useState(user.invitation?.cohort || "prelaunch");
  const [inviteStatus, setInviteStatus] = useState(user.invitation?.invite_status || "pending");
  const [notes, setNotes] = useState(user.notes || "");

  useEffect(() => {
    setCohort(user.invitation?.cohort || "prelaunch");
    setInviteStatus(user.invitation?.invite_status || "pending");
    setNotes(user.notes || "");
  }, [user.user_id]);

  const save = () => onPatch(user.user_id, { cohort, invite_status: inviteStatus, notes });

  return (
    <div style={{
      position:"fixed", top:0, right:0, bottom:0, width:"min(480px, 90vw)",
      background:"#FAF8F3", borderLeft:"1px solid rgba(184,145,58,0.35)",
      boxShadow:"-24px 0 60px rgba(0,0,0,0.16)", padding:"26px 28px", overflowY:"auto",
      fontFamily:"Jost, sans-serif", zIndex:2000,
    }} data-testid="alpha-user-drawer">
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16}}>
        <div>
          <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.55)"}}>Member</div>
          <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 0", fontWeight:500}}>{user.name}</h2>
          <div style={{fontSize:12, color:"rgba(27,35,64,0.7)", marginTop:2}}>{user.email}</div>
          <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:2}}>{user.city || "(no city)"} · {user.profession_type || "(no profession)"}</div>
        </div>
        <button type="button" onClick={onClose} data-testid="alpha-user-drawer-close"
          style={{background:"none", border:"none", fontSize:22, cursor:"pointer", color:"rgba(27,35,64,0.6)"}}>×</button>
      </div>

      <Row label="User ID" value={user.user_id} mono />
      <Row label="Joined" value={dateOnly(user.created_at)} />
      <Row label="Role" value={user.role} />
      <Row label="Founding member" value={user.founding_member ? "yes" : "no"} />

      <Section title="Activity" />
      <Row label="Last login" value={user.activity?.last_login_at ? `${dateOnly(user.activity.last_login_at)} ${clockTime(user.activity.last_login_at)}` : "—"} />
      <Row label="First booking" value={dateOnly(user.activity?.first_booking_at)} />
      <Row label="First message" value={dateOnly(user.activity?.first_message_at)} />
      <Row label="First post" value={dateOnly(user.activity?.first_post_at)} />
      <Row label="Total sessions" value={user.activity?.total_sessions || 0} />

      <Section title="Counts" />
      <Row label="Bookings" value={user.counts?.bookings || 0} />
      <Row label="Community posts" value={user.counts?.community_posts || 0} />
      <Row label="Messages" value={user.counts?.messages || 0} />
      <Row label="Bug reports" value={user.counts?.bug_reports || 0} />
      <Row label="Feature requests" value={user.counts?.feature_requests || 0} />
      <Row label="Profile completion" value={`${user.profile_completion_pct}%`} />
      <Row label="Legal accepted" value={user.legal_ok ? "yes" : "not yet"} />

      <Section title="Device" />
      <Row label="Platform" value={user.device?.platform} />
      <Row label="App version" value={user.device?.app_version || "—"} />
      <Row label="Last UA" value={short(user.device?.last_ua, 46) || "—"} mono />

      <Section title="Invitation & Notes" />
      <div style={{display:"grid", gap:10, marginTop:6}}>
        <label style={{fontSize:12}}>
          Cohort
          <select value={cohort} onChange={(e) => setCohort(e.target.value)}
            data-testid="drawer-cohort"
            style={{width:"100%", padding:"8px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13}}>
            {COHORTS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={{fontSize:12}}>
          Invite status
          <select value={inviteStatus} onChange={(e) => setInviteStatus(e.target.value)}
            data-testid="drawer-invite-status"
            style={{width:"100%", padding:"8px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13}}>
            {INVITE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label style={{fontSize:12}}>
          Internal notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5}
            data-testid="drawer-notes"
            style={{width:"100%", padding:"10px 12px", marginTop:4, border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13, fontFamily:"Jost", resize:"vertical"}}
            placeholder="Any context Cam should remember about this tester…" />
        </label>
        <button type="button" onClick={save} disabled={busy}
          data-testid="drawer-save"
          style={{padding:"10px 20px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, cursor:"pointer", fontSize:13, fontWeight:500}}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function Section({title}) {
  return <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", margin:"18px 0 6px", fontWeight:600}}>{title}</div>;
}
function Row({label, value, mono}) {
  return (
    <div style={{display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid rgba(27,35,64,0.05)", gap:12}}>
      <span style={{fontSize:12, color:"rgba(27,35,64,0.6)"}}>{label}</span>
      <span style={{fontSize:12, fontFamily: mono ? "ui-monospace, monospace" : "inherit", color:"#1B2340", textAlign:"right", wordBreak:"break-all"}}>{String(value ?? "—")}</span>
    </div>
  );
}


function BulkInviteModal({ onClose, onSuccess }) {
  const [emails, setEmails] = useState("");
  const [cohort, setCohort] = useState("wave1");
  const [sendEmail, setSendEmail] = useState(true);
  const [invitedByName, setInvitedByName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const parsedPreview = useMemo(() => {
    const found = (emails.match(/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g) || [])
      .map(e => e.toLowerCase());
    return Array.from(new Set(found));
  }, [emails]);

  const submit = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post(`/admin/alpha-invites/bulk`, {
        emails,
        cohort,
        send_email: sendEmail,
        invited_by_name: invitedByName || null,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk invite failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (result) { onSuccess(); } else { onClose(); }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(27,35,64,0.55)", zIndex:2100,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24,
    }} onClick={handleClose} data-testid="bulk-invite-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="bulk-invite-modal"
        style={{
          background:"#FAF8F3", borderRadius:14, width:"min(720px, 100%)",
          maxHeight:"90vh", overflow:"auto", padding:"28px 30px",
          border:"1px solid rgba(184,145,58,0.35)", boxShadow:"0 30px 80px rgba(0,0,0,0.28)",
          fontFamily:"Jost, sans-serif", color:"#1B2340",
        }}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14}}>
          <div>
            <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Alpha Ops</div>
            <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 0", fontWeight:500}}>Bulk Invite</h2>
            <div style={{fontSize:12, color:"rgba(27,35,64,0.65)", marginTop:4}}>
              Paste up to 200 emails. We&#39;ll dedupe, tag them with a cohort, and fire the welcome email.
            </div>
          </div>
          <button type="button" onClick={handleClose} data-testid="bulk-invite-close"
            style={{background:"none", border:"none", fontSize:22, cursor:"pointer", color:"rgba(27,35,64,0.6)"}}>×</button>
        </div>

        {!result && (
          <>
            <label style={{fontSize:12, color:"rgba(27,35,64,0.7)", display:"block", marginBottom:4}}>Emails</label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              rows={8}
              placeholder={"alex@example.com, jamie@example.com\nsam@example.com"}
              data-testid="bulk-invite-emails"
              style={{
                width:"100%", padding:"12px 14px", border:"1px solid rgba(27,35,64,0.22)",
                borderRadius:10, fontSize:13, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace",
                resize:"vertical", background:"#fff",
              }}
            />
            <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:4, minHeight:16}} data-testid="bulk-invite-count">
              {parsedPreview.length > 0
                ? `${parsedPreview.length} address${parsedPreview.length === 1 ? "" : "es"} detected`
                : "Commas, semicolons, spaces, or newlines are all fine."}
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:14}}>
              <label style={{fontSize:12, color:"rgba(27,35,64,0.7)"}}>
                Cohort
                <select value={cohort} onChange={(e) => setCohort(e.target.value)}
                  data-testid="bulk-invite-cohort"
                  style={{width:"100%", padding:"9px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.22)", borderRadius:8, fontSize:13, background:"#fff"}}>
                  {COHORTS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{fontSize:12, color:"rgba(27,35,64,0.7)"}}>
                From name <span style={{color:"rgba(27,35,64,0.4)"}}>(optional)</span>
                <input value={invitedByName} onChange={(e) => setInvitedByName(e.target.value)}
                  placeholder="e.g. Cam @ Métier"
                  data-testid="bulk-invite-from"
                  style={{width:"100%", padding:"9px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.22)", borderRadius:8, fontSize:13, background:"#fff"}} />
              </label>
            </div>

            <label style={{display:"flex", alignItems:"center", gap:8, marginTop:14, fontSize:13, cursor:"pointer"}}>
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                data-testid="bulk-invite-send-email" />
              Send the welcome email now (uncheck to just create records)
            </label>

            {err && <div data-testid="bulk-invite-error"
              style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:14, fontSize:13}}>
              {err}
            </div>}

            <div style={{display:"flex", gap:12, justifyContent:"flex-end", marginTop:18}}>
              <button type="button" onClick={onClose}
                style={{padding:"10px 18px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy || parsedPreview.length === 0}
                data-testid="bulk-invite-submit"
                style={{
                  padding:"10px 22px", background: busy || parsedPreview.length === 0 ? "rgba(184,145,58,0.5)" : "#B8913A",
                  color:"#FAF8F3", border:"none", borderRadius:999,
                  cursor: busy || parsedPreview.length === 0 ? "not-allowed" : "pointer",
                  fontSize:13, fontWeight:500,
                }}>
                {busy ? "Sending…" : `Invite ${parsedPreview.length || ""}`.trim()}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="bulk-invite-result">
            <div style={{
              background:"#EFF6EB", border:"1px solid #C7E0B5", borderRadius:10,
              padding:"14px 16px", marginBottom:14,
            }}>
              <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                Done — {result.counts.total} address{result.counts.total === 1 ? "" : "es"} processed
              </div>
              <div style={{fontSize:13, color:"rgba(27,35,64,0.75)", lineHeight:1.7}}>
                <b>{result.counts.created}</b> new invite{result.counts.created === 1 ? "" : "s"} ·{" "}
                <b>{result.counts.reinvited}</b> re-invited ·{" "}
                <b>{result.counts.existing_user}</b> already members ·{" "}
                <b>{result.counts.email_sent}</b> email{result.counts.email_sent === 1 ? "" : "s"} sent
                {result.counts.email_failed > 0 && <> · <b style={{color:"#B84A3A"}}>{result.counts.email_failed} failed</b></>}
                {result.counts.email_skipped > 0 && <> · <b>{result.counts.email_skipped} skipped</b></>}
              </div>
            </div>

            <div style={{maxHeight:280, overflow:"auto", border:"1px solid rgba(27,35,64,0.1)", borderRadius:10, background:"#fff"}}>
              <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                <thead>
                  <tr style={{background:"rgba(184,145,58,0.08)", textAlign:"left"}}>
                    <th style={{padding:"8px 10px"}}>Email</th>
                    <th style={{padding:"8px 10px"}}>Status</th>
                    <th style={{padding:"8px 10px"}}>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={i} style={{borderTop:"1px solid rgba(27,35,64,0.06)"}} data-testid={`bulk-invite-row-${i}`}>
                      <td style={{padding:"7px 10px", fontFamily:"ui-monospace, monospace"}}>{r.email}</td>
                      <td style={{padding:"7px 10px"}}>
                        <Pill label={r.status?.replace("_", " ")}
                          accent={r.status === "existing_user"} />
                      </td>
                      <td style={{padding:"7px 10px", color: r.email_delivery === "failed" ? "#B84A3A" : "rgba(27,35,64,0.7)"}}>
                        {r.email_delivery || "—"}
                        {r.error && <div style={{fontSize:11, color:"#B84A3A"}}>{r.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{display:"flex", gap:12, justifyContent:"flex-end", marginTop:18}}>
              <button type="button" onClick={() => { setResult(null); setEmails(""); }}
                data-testid="bulk-invite-another"
                style={{padding:"10px 18px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                Send another batch
              </button>
              <button type="button" onClick={onSuccess}
                data-testid="bulk-invite-done"
                style={{padding:"10px 22px", background:"#1B2340", color:"#FAF8F3", border:"none", borderRadius:999, cursor:"pointer", fontSize:13, fontWeight:500}}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Session 56.1 — Wave 1 Roster dashboard tiles
// ---------------------------------------------------------------------------

const DELIVERY_COLORS = {
  delivered: "#5C8A44",
  opened: "#3E8B60",
  clicked: "#316E4B",
  sent: "#8A6B26",
  pending: "rgba(27,35,64,0.55)",
  delayed: "#B8913A",
  bounced: "#B84A3A",
  complained: "#B84A3A",
  failed: "#B84A3A",
  "n/a": "rgba(27,35,64,0.4)",
};

function DeliveryPill({ status }) {
  const label = status || "—";
  const color = DELIVERY_COLORS[label] || "rgba(27,35,64,0.7)";
  return (
    <span data-testid={`delivery-pill-${label}`} style={{
      display:"inline-block",
      padding:"1px 8px",
      borderRadius:999,
      fontSize:10.5,
      letterSpacing:"0.04em",
      textTransform:"uppercase",
      border:`1px solid ${color}55`,
      color,
      background:`${color}12`,
    }}>{label}</span>
  );
}

function WaveRoster({ roster, cohort, onCohortChange, onRefresh }) {
  const t = roster?.totals;
  const timing = roster?.timing;
  const delivery = roster?.delivery;

  const tileStyle = {
    background:"#FAF8F3",
    border:"1px solid rgba(184,145,58,0.22)",
    borderRadius:12,
    padding:"14px 18px",
    minWidth:150,
    flex:"1 1 150px",
  };
  const numStyle = {
    fontFamily:"Cormorant Garamond, serif",
    fontSize:30,
    fontWeight:500,
    lineHeight:1.05,
    margin:"2px 0",
    color:"#1B2340",
  };
  const lblStyle = {
    fontSize:11,
    letterSpacing:"0.14em",
    textTransform:"uppercase",
    color:"rgba(27,35,64,0.6)",
    fontWeight:600,
  };
  const subStyle = { fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:2 };

  return (
    <div data-testid="wave-roster" style={{marginBottom:18}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
        <div style={{display:"flex", gap:10, alignItems:"baseline"}}>
          <span style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Cohort Dashboard</span>
          <select
            value={cohort}
            onChange={(e) => onCohortChange(e.target.value)}
            data-testid="roster-cohort-select"
            style={{padding:"6px 10px", border:"1px solid rgba(27,35,64,0.18)", borderRadius:8, fontSize:12, background:"#fff", fontFamily:"Jost, sans-serif"}}
          >
            {COHORTS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button type="button" onClick={onRefresh} data-testid="roster-refresh"
          style={{background:"none", border:"none", color:"#B8913A", fontSize:12, cursor:"pointer"}}>
          ↻ refresh
        </button>
      </div>

      <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
        <div style={tileStyle} data-testid="roster-tile-invited">
          <div style={lblStyle}>Invited</div>
          <div style={numStyle}>{t?.invited ?? "—"}</div>
          <div style={subStyle}>{t?.pending ?? 0} pending</div>
        </div>
        <div style={tileStyle} data-testid="roster-tile-accepted">
          <div style={lblStyle}>Accepted</div>
          <div style={numStyle}>{t?.accepted ?? "—"}</div>
          <div style={subStyle}>{t?.acceptance_pct ?? 0}% acceptance</div>
        </div>
        <div style={tileStyle} data-testid="roster-tile-first-booking">
          <div style={lblStyle}>First bookings</div>
          <div style={numStyle}>{t?.first_booking_count ?? "—"}</div>
          <div style={subStyle}>
            {t?.first_booking_pct ?? 0}% of accepted
          </div>
        </div>
        <div style={tileStyle} data-testid="roster-tile-timing">
          <div style={lblStyle}>Avg days since invite</div>
          <div style={numStyle}>
            {timing?.avg_days_since_invite != null ? timing.avg_days_since_invite : "—"}
          </div>
          <div style={subStyle}>
            {timing?.avg_days_to_accept != null
              ? `${timing.avg_days_to_accept}d to accept`
              : "—"}
          </div>
        </div>
        <div style={tileStyle} data-testid="roster-tile-delivery">
          <div style={lblStyle}>Email delivery</div>
          <div style={numStyle}>{delivery?.delivered_pct ?? 0}%</div>
          <div style={subStyle}>
            {delivery?.counts?.delivered || 0} delivered · {delivery?.counts?.opened || 0} opened · {delivery?.counts?.bounced || 0} bounced
          </div>
        </div>
      </div>

      {roster?.roster?.length > 0 && (
        <details data-testid="roster-details" style={{marginTop:12, background:"#fff", border:"1px solid rgba(27,35,64,0.08)", borderRadius:10}}>
          <summary style={{padding:"10px 14px", cursor:"pointer", fontSize:12, color:"rgba(27,35,64,0.75)"}}>
            {cohort.toUpperCase()} roster · {roster.roster.length} members — click to expand
          </summary>
          <div style={{maxHeight:280, overflow:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
              <thead>
                <tr style={{background:"rgba(184,145,58,0.06)", textAlign:"left"}}>
                  <th style={{padding:"7px 10px"}}>Email</th>
                  <th style={{padding:"7px 10px"}}>Status</th>
                  <th style={{padding:"7px 10px"}}>Delivery</th>
                  <th style={{padding:"7px 10px"}}>Invited</th>
                  <th style={{padding:"7px 10px"}}>Days</th>
                  <th style={{padding:"7px 10px"}}>First booking</th>
                </tr>
              </thead>
              <tbody>
                {roster.roster.map((row, i) => (
                  <tr key={i} style={{borderTop:"1px solid rgba(27,35,64,0.05)"}} data-testid={`roster-row-${i}`}>
                    <td style={{padding:"6px 10px", fontFamily:"ui-monospace, monospace"}}>{row.email}</td>
                    <td style={{padding:"6px 10px"}}><Pill label={row.status} accent={row.status === "accepted"} /></td>
                    <td style={{padding:"6px 10px"}}><DeliveryPill status={row.email_status} /></td>
                    <td style={{padding:"6px 10px", color:"rgba(27,35,64,0.7)"}}>{dateOnly(row.invited_at)}</td>
                    <td style={{padding:"6px 10px", color:"rgba(27,35,64,0.7)"}}>{row.days_since_invite ?? "—"}</td>
                    <td style={{padding:"6px 10px", color:"rgba(27,35,64,0.7)"}}>{dateOnly(row.first_booking_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
